import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, PackageCheck, PackageSearch } from 'lucide-react';
import { Card, CardContent } from '@components/Card';
import { DashboardDemoUnpaidCustomers } from '@/types/dashboard';
import { formatCurrencyBRL, formatDateBR } from '@pages/financial/financialUtils';

/**
 * Painel "Clientes com demonstração sem pagamento" (admin/finance).
 * Cada linha abre a conciliação de demonstração da cliente.
 */
export const DemoUnpaidCustomersPanel: React.FC<{ data: DashboardDemoUnpaidCustomers }> = ({ data }) => (
  <Card>
    <CardContent className="p-0">
      {data.total_customers === 0 ? (
        <div className="p-4 flex items-start gap-3">
          <PackageCheck className="text-emerald-600 flex-shrink-0 mt-0.5" size={20} />
          <p className="text-sm font-medium text-emerald-800">Nenhuma cliente com demonstração pendente</p>
        </div>
      ) : (
        <>
          <div className="p-4 border-b border-slate-200 flex items-start gap-3">
            <PackageSearch className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
            <p className="text-sm font-medium text-amber-800">
              {data.total_customers} cliente(s) com demonstração sem pagamento
              {data.total_customers > data.customers.length && ` — exibindo ${data.customers.length}`}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left font-medium px-4 py-2">Cliente</th>
                  <th className="text-right font-medium px-4 py-2">Nº pedidos</th>
                  <th className="text-right font-medium px-4 py-2">Valor</th>
                  <th className="text-left font-medium px-4 py-2">Desde</th>
                  <th className="text-right font-medium px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.customers.map((c) => (
                  <tr key={c.customer_id} className="hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <Link to={`/demo-reconciliation/${c.customer_id}`} className="text-slate-800 hover:underline">
                        {c.customer_name}
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-right text-slate-700">{c.orders_count}</td>
                    <td className="px-4 py-2 text-right text-slate-800 whitespace-nowrap">
                      {formatCurrencyBRL(c.products_total)}
                    </td>
                    <td className="px-4 py-2 text-slate-700">{formatDateBR(c.oldest_order_date)}</td>
                    <td className="px-4 py-2 text-right">
                      <Link
                        to={`/demo-reconciliation/${c.customer_id}`}
                        className="inline-flex items-center gap-1 text-sm font-medium text-emerald-700 hover:text-emerald-800"
                      >
                        Conciliar
                        <ArrowRight size={14} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <div className="px-4 py-3 border-t border-slate-200">
        <Link to="/demo-reconciliation" className="text-sm font-medium text-blue-700 hover:underline">
          Abrir Conciliação de Demonstração →
        </Link>
      </div>
    </CardContent>
  </Card>
);
